import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Button, Alert, Select, Space } from 'antd';
import {
  AudioOutlined,
  CheckCircleFilled,
  LoadingOutlined,
  WarningFilled,
  ReloadOutlined,
  SoundOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { BrowserInfo } from '../hooks/useBrowserDetection';
import useAudioLevel from '../hooks/useAudioLevel';
import useSpeechRecognition from '../hooks/useSpeechRecognition';
import HelpAccordions from '../components/HelpAccordions';
import '../DeviceSetupWizard.scss';

interface AudioDevice {
  deviceId: string;
  label: string;
}

interface MicrophoneSetupStepProps {
  onComplete: () => void;
  cameraStream: MediaStream | null;
  isCompleted: boolean;
  browserInfo: BrowserInfo;
}

const MicrophoneSetupStep: React.FC<MicrophoneSetupStepProps> = ({
  onComplete,
  cameraStream,
  isCompleted,
  browserInfo,
}) => {
  const [micState, setMicState] = useState<'initializing' | 'ready' | 'testing' | 'error'>('initializing');
  const [errorMessage, setErrorMessage] = useState('');
  const [micStream, setMicStream] = useState<MediaStream | null>(null);
  const [audioDevices, setAudioDevices] = useState<AudioDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [isTesting, setIsTesting] = useState(false);

  const { audioLevel, startAnalyzing, stopAnalyzing } = useAudioLevel();
  const {
    transcript,
    interimTranscript,
    isListening,
    isSupported: speechSupported,
    error: speechError,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechRecognition();

  // Enumerate available audio devices
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices
        .filter(device => device.kind === 'audioinput')
        .map((device, index) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${index + 1}`,
        }));
      
      setAudioDevices(audioInputs);
      
      // Set default device if not already selected
      if (!selectedDeviceId && audioInputs.length > 0) {
        setSelectedDeviceId(audioInputs[0].deviceId);
      }
    } catch (error) {
      console.error('Error enumerating devices:', error);
    }
  }, [selectedDeviceId]);

  // Start microphone with selected device
  const startMicrophone = useCallback(async (deviceId?: string) => {
    setMicState('initializing');
    setErrorMessage('');

    try {
      // Stop existing stream if any
      if (micStream) {
        micStream.getTracks().forEach(track => track.stop());
        stopAnalyzing();
      }

      const constraints: MediaStreamConstraints = {
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
      };

      const audioStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      setMicStream(audioStream);
      startAnalyzing(audioStream);
      setMicState('ready');
      
      // Re-enumerate devices to get proper labels after permission granted
      await enumerateDevices();
      
      // Auto-complete since microphone test is optional
      onComplete();
    } catch (error: any) {
      console.error('Microphone access error:', error);
      
      if (error.name === 'NotAllowedError') {
        setMicState('error');
        setErrorMessage('Microphone access was denied. Please allow microphone access.');
      } else if (error.name === 'NotFoundError') {
        setMicState('error');
        setErrorMessage('No microphone found. Please connect a microphone.');
      } else if (error.name === 'OverconstrainedError') {
        // Device not found, try with default
        setMicState('error');
        setErrorMessage('Selected microphone not available. Please choose another device.');
      } else {
        setMicState('error');
        setErrorMessage('Failed to access microphone. Please check your settings.');
      }
    }
  }, [micStream, startAnalyzing, stopAnalyzing, enumerateDevices, onComplete]);

  // Handle device change
  const handleDeviceChange = useCallback((deviceId: string) => {
    setSelectedDeviceId(deviceId);
    setIsTesting(false);
    stopListening();
    resetTranscript();
    startMicrophone(deviceId);
  }, [startMicrophone, stopListening, resetTranscript]);

  // Initialize on mount
  useEffect(() => {
    enumerateDevices();
    startMicrophone();

    return () => {
      stopAnalyzing();
      stopListening();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start/Stop test
  const handleStartTest = () => {
    setIsTesting(true);
    resetTranscript();
    if (speechSupported) {
      startListening();
    }
  };

  const handleStopTest = () => {
    setIsTesting(false);
    stopListening();
  };

  const handleRetry = () => {
    resetTranscript();
    setIsTesting(false);
    startMicrophone(selectedDeviceId);
  };

  // Generate simple audio wave visualization
  const renderAudioWave = () => {
    const barCount = 40;
    const bars = [];
    const normalizedLevel = Math.min(100, audioLevel) / 100;
    const isActive = normalizedLevel > 0.1;

    for (let i = 0; i < barCount; i++) {
      // Create smooth wave pattern - center bars taller
      const centerDistance = Math.abs(i - barCount / 2) / (barCount / 2);
      const waveBase = Math.cos(centerDistance * Math.PI * 0.5); // Smooth cosine curve
      
      // Height calculation
      const minHeight = 4;
      const maxHeight = isActive ? 35 + normalizedLevel * 25 : 20;
      const height = minHeight + (maxHeight - minHeight) * waveBase * (isActive ? (0.6 + Math.random() * 0.4) : 0.4);

      bars.push(
        <div
          key={i}
          className={`audio-bar ${isActive ? 'active' : 'idle'}`}
          style={{ 
            height: `${height}px`,
            animationDelay: `${i * 0.02}s`,
          }}
        />
      );
    }

    return <div className="audio-wave-bars">{bars}</div>;
  };

  return (
    <div className="setup-step microphone-setup-step">
      {/* Header */}
      <div className="step-header">
        <Typography.Title level={5} className="step-title">
          <AudioOutlined /> Microphone Setup
        </Typography.Title>
        <Typography.Text className="step-description" type="secondary">
          Select your microphone and optionally test it by speaking
        </Typography.Text>
      </div>

      {/* Main Content */}
      <div className="step-main">
        <div className="step-content">

          {micState === 'error' ? (
            <Alert
              type="error"
              message="Microphone Error"
              description={errorMessage}
              showIcon
              action={
                <Button size="small" onClick={handleRetry} icon={<ReloadOutlined />}>
                  Retry
                </Button>
              }
            />
          ) : (
            <>
              {/* Device Selection */}
              <div style={{ marginBottom: 24 }}>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Select Microphone
                </Typography.Text>
                <Select
                  style={{ width: '100%', maxWidth: 400 }}
                  value={selectedDeviceId}
                  onChange={handleDeviceChange}
                  loading={micState === 'initializing'}
                  disabled={micState === 'initializing'}
                  placeholder="Select a microphone"
                >
                  {audioDevices.map(device => (
                    <Select.Option key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              {/* Simple Audio Wave Visualization */}
              <div className="audio-wave-wrapper">
                {renderAudioWave()}
                <div className="audio-wave-status">
                  {micState === 'initializing' ? (
                    <><LoadingOutlined spin /> Starting microphone...</>
                  ) : audioLevel > 10 ? (
                    <><SoundOutlined /> Listening...</>
                  ) : (
                    <><CheckCircleFilled style={{ color: '#52c41a' }} /> Microphone ready</>
                  )}
                </div>
              </div>

              {/* Optional Speech Test */}
              {speechSupported && (
                <div className="speech-test-container">
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <Typography.Text type="secondary">
                      Want to test your microphone? Click the button below and speak.
                    </Typography.Text>
                  </div>

                  {/* Test Button */}
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    {!isTesting ? (
                      <Button
                        type="primary"
                        icon={<AudioOutlined />}
                        onClick={handleStartTest}
                        ghost
                      >
                        Start Microphone Test
                      </Button>
                    ) : (
                      <Button
                        danger
                        icon={<WarningFilled />}
                        onClick={handleStopTest}
                      >
                        Stop Test
                      </Button>
                    )}
                  </div>

                  {/* Transcript - only show when testing */}
                  {isTesting && (
                    <div className="transcript-box">
                      <div className="transcript-label">
                        {isListening ? (
                          <><LoadingOutlined spin /> Listening... (speak anything)</>
                        ) : (
                          'What we heard:'
                        )}
                      </div>
                      <div className="transcript-text">
                        {transcript || interimTranscript ? (
                          <>
                            {transcript}
                            {interimTranscript && (
                              <span className="interim"> {interimTranscript}</span>
                            )}
                          </>
                        ) : (
                          <span className="transcript-placeholder">
                            Start speaking to see your words here...
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {speechError && isTesting && (
                    <Alert
                      type="warning"
                      message={speechError}
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}
                </div>
              )}

              {/* Ready Message */}
              {micState === 'ready' && !isTesting && (
                <Alert
                  type="success"
                  message="Microphone Ready!"
                  description="Your microphone is configured. Click Continue to proceed to the next step."
                  showIcon
                  icon={<CheckCircleFilled />}
                  style={{ marginTop: 16 }}
                />
              )}
            </>
          )}
        </div>

        {/* Help Section */}
        <div className="step-help">
          <HelpAccordions type="microphone" browserInfo={browserInfo} />
        </div>
      </div>
    </div>
  );
};

export default MicrophoneSetupStep;
